import React, { type FC } from "react"
import { useTranslation } from "react-i18next"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Errors } from "pages/Products/ProductPage/Errors"

interface SelectWithInfoTooltipProps {
  onChange: (e: any) => void
  value: string
  label: string
  disabled?: boolean
  isOptional?: boolean
  children: React.ReactNode
  name?: string
  placeholder?: string
  style?: any
  errors?: string[]
  testId?: string
}

export const SelectWithInfoTooltip: FC<SelectWithInfoTooltipProps> = ({
  onChange,
  label,
  value,
  disabled = false,
  isOptional,
  children,
  errors,
  name: _name = label,
  placeholder,
  style: _style,
  testId,
}) => {
  const { t } = useTranslation()

  // Extract options from children (React elements with value/children props)
  const options: Array<{ value: string; label: string }> = []
  React.Children.forEach(children, (child) => {
    if (
      React.isValidElement<{ value?: string; children?: React.ReactNode }>(child) &&
      child.props.value !== undefined
    )
      options.push({
        value: child.props.value,
        label:
          typeof child.props.children === "string"
            ? child.props.children
            : String(child.props.children),
      })
  })

  return (
    <div className="min-w-[150px]" data-testid={testId ? `${testId}-field` : undefined}>
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-row items-center gap-1">
          <Label>{label}</Label>
          {isOptional && (
            <span className="text-center font-medium text-xs text-muted-foreground">
              {t("selectWithInfoTooltip.optional")}
            </span>
          )}
        </div>
        <Select disabled={disabled} value={value} onValueChange={onChange}>
          <SelectTrigger data-testid={testId}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent data-testid={testId ? `${testId}-options` : undefined}>
            {options.map((opt) => (
              <SelectItem
                key={opt.value}
                value={opt.value}
                data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
              >
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Errors _errors={errors} />
    </div>
  )
}
